import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Heart } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await addToCart(product.id, 1);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  const discountPercentage = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <Link
      to={`/product/${product.id}`}
      className="group product-card bg-white rounded-2xl border border-[#6B4F2A]/10 overflow-hidden"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-[#FFF8E7]">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover product-image transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {discountPercentage > 0 && (
            <span className="badge badge-sale" data-testid="product-sale-badge">
              -{discountPercentage}%
            </span>
          )}
          {product.is_bestseller && (
            <span className="badge badge-bestseller" data-testid="product-bestseller-badge">
              Bestseller
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-[#E0A106] hover:text-white transition-colors"
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className="w-4 h-4" />
          </button>
        </div>

        {/* Add to Cart Button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <button
            onClick={handleAddToCart}
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3"
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4" />
            Add to Cart
          </button>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4">
        {/* Category */}
        <p className="text-xs uppercase tracking-wider text-[#1F7A63] font-semibold mb-1" data-testid="product-category">
          {product.category}
        </p>

        {/* Name */}
        <h3 className="font-semibold text-[#2C1E0F] mb-2 group-hover:text-[#1F7A63] transition-colors line-clamp-2" data-testid="product-name">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.floor(product.rating) ? 'text-[#E0A106] fill-[#E0A106]' : 'text-[#6B4F2A]/30'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-[#574021]" data-testid="product-reviews-count">
            ({product.reviews_count})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-[#1F7A63]" data-testid="product-price">
            Rs. {product.price.toLocaleString()}
          </span>
          {product.original_price && (
            <span className="text-sm text-[#574021]/50 line-through" data-testid="product-original-price">
              Rs. {product.original_price.toLocaleString()}
            </span>
          )}
        </div>

        {/* Weight */}
        <p className="text-xs text-[#574021]/70 mt-1" data-testid="product-weight">
          {product.weight}
        </p>
      </div>
    </Link>
  );
};

export default ProductCard;
